import { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  UsersIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ClipboardCheckIcon,
  CheckCircleIcon,
  CashIcon,
  XCircleIcon,
  CreditCardIcon,
  BriefcaseIcon,
  LockClosedIcon
} from '@heroicons/react/outline';
import moment from 'moment';
import { useLanguage } from '../contexts/LanguageContext';
import GoverningBodyDashboard from '../components/GoverningBodyDashboard';
import AdminDashboard from '../components/AdminDashboard';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalClients: 0,
    totalDocuments: 0,
    totalLimitOfIndemnity: 0,
    totalPremium: 0,
    totalPaid: 0,
    totalUnpaid: 0,
    paymentMethodBreakdown: {
      cash: 0,
      cheque: 0,
      onAccount: 0
    },
    totalPolicies: 0,
    paidPolicies: 0,
    unpaidPolicies: 0,
    policyDocuments: 0,
    totalClaims: 0,
    settledClaims: 0,
    recentDocuments: []
  });
  const [documentStats, setDocumentStats] = useState({
    totalThisYear: 0,
    totalOverall: 0,
    currentYear: new Date().getFullYear(),
    recentDocuments: []
  });
  const { t } = useLanguage();

  useEffect(() => {
    fetchDashboardStats();
    fetchDocumentStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/api/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchDocumentStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/documents/stats', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setDocumentStats(response.data);
    } catch (error) {
      console.error('Failed to fetch document stats:', error);
    }
  };

  // Calculate document generation percentage (documents from paid policies)
  const paymentCompletionRate = stats.totalPolicies ? 
    Math.round((stats.paidPolicies / stats.totalPolicies) * 100) : 0;

  // Show governing body dashboard for governing body users
  if (user?.role === 'governing_body') {
    return <GoverningBodyDashboard />;
  }

  // Show admin dashboard for admin users
  if (user?.role === 'admin') {
    return <AdminDashboard />;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-semibold text-gray-900">{t('navigation.dashboard')}</h1>
      <div className="mt-6">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {/* Total Clients Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UsersIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('dashboard.totalClients')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.totalClients}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Total Documents Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentTextIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('dashboard.totalDocuments')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.totalDocuments}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Total Limit of Indemnity Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CurrencyDollarIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('dashboard.totalCoverageAmount')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {new Intl.NumberFormat('en', {
                        style: 'currency',
                        currency: 'LYD'
                      }).format(stats.totalLimitOfIndemnity)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Total Premium Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CashIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('claims.premium')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {new Intl.NumberFormat('en', {
                        style: 'currency',
                        currency: 'LYD'
                      }).format(stats.totalPremium)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Total Paid Amounts Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('dashboard.totalPaid')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {new Intl.NumberFormat('en', {
                        style: 'currency',
                        currency: 'LYD'
                      }).format(stats.totalPaid)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Total Unpaid Amounts Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XCircleIcon className="h-6 w-6 text-red-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('dashboard.totalUnpaid')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {new Intl.NumberFormat('en', {
                        style: 'currency',
                        currency: 'LYD'
                      }).format(stats.totalUnpaid)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Total Policies Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentTextIcon className="h-6 w-6 text-blue-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('dashboard.totalPolicies')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.totalPolicies || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Paid vs Unpaid Policies Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <LockClosedIcon className="h-6 w-6 text-indigo-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('dashboard.paymentCompletionRate')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {paymentCompletionRate}% ({stats.paidPolicies}/{stats.totalPolicies})
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Total Claims Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClipboardCheckIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('claims.title')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.totalClaims || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Documents This Year Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentTextIcon className="h-6 w-6 text-purple-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Documents {documentStats.currentYear}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {documentStats.totalThisYear}
                    </dd>
                    <dd className="text-xs text-gray-500">
                      Total: {documentStats.totalOverall}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Settled Claims Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {t('claims.settled')}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.settledClaims || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Method Breakdown */}
        <div className="mt-8">
          <h2 className="text-lg font-medium text-gray-900">{t('dashboard.paymentMethodBreakdown')}</h2>
          <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {/* Cash Payments Card */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CashIcon className="h-6 w-6 text-green-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {t('dashboard.cashPayments')}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {new Intl.NumberFormat('en', {
                          style: 'currency',
                          currency: 'LYD'
                        }).format(stats.paymentMethodBreakdown?.cash || 0)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Cheque Payments Card */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CreditCardIcon className="h-6 w-6 text-blue-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {t('dashboard.chequePayments')}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {new Intl.NumberFormat('en', {
                          style: 'currency',
                          currency: 'LYD'
                        }).format(stats.paymentMethodBreakdown?.cheque || 0)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* On Account Payments Card */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <BriefcaseIcon className="h-6 w-6 text-purple-500" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {t('dashboard.onAccountPayments')}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {new Intl.NumberFormat('en', {
                          style: 'currency',
                          currency: 'LYD'
                        }).format(stats.paymentMethodBreakdown?.onAccount || 0)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Document Generation Status */}
        <div className="mt-8">
          <h2 className="text-lg font-medium text-gray-900">{t('dashboard.documentGenerationStatus')}</h2>
          <div className="mt-4 bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="mb-4">
                <p className="text-sm text-gray-700">
                  <strong>{t('common.note')}:</strong> {t('dashboard.documentsGenerationNote')}
                </p>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">{t('dashboard.policyPaymentStatus')}</h3>
                  <div className="mt-2 flex items-center">
                    <div className="flex-1">
                      <div className="relative pt-1">
                        <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                          <div
                            style={{ width: `${paymentCompletionRate}%` }}
                            className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500"
                          ></div>
                        </div>
                      </div>
                    </div>
                    <div className="ml-3 text-sm text-gray-900">
                      {paymentCompletionRate}% {t('dashboard.percentagePaid')}
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div className="text-sm">
                      <span className="text-green-500 font-semibold">{stats.paidPolicies}</span> {t('dashboard.paidPolicies')}
                    </div>
                    <div className="text-sm">
                      <span className="text-red-500 font-semibold">{stats.unpaidPolicies}</span> {t('dashboard.unpaidPolicies')}
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">{t('dashboard.documentGeneration')}</h3>
                  <p className="mt-2 text-3xl font-bold text-gray-900">
                    {stats.policyDocuments} <span className="text-sm font-medium text-gray-500">{t('dashboard.of')}</span> {stats.paidPolicies}
                  </p>
                  <p className="text-sm text-gray-500">{t('dashboard.policyDocumentsGenerated')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Documents Table */}
        <div className="mt-8">
          <h2 className="text-lg font-medium text-gray-900">{t('dashboard.recentDocuments')}</h2>
          <div className="mt-4 bg-white shadow overflow-hidden sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('dashboard.documentNumber')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('dashboard.client')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('dashboard.date')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.recentDocuments?.map((doc) => (
                  <tr key={doc.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {doc.documentNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {doc.client 
                        ? `${doc.client.firstName} ${doc.client.lastName}`
                        : doc.clientData?.insuredName || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {moment(doc.createdAt).format('MMM D, YYYY')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}